import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Chip, IconButton, TextField, Tooltip } from "@mui/material";
import { InboxOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import { format } from "date-fns";

import checarData from "../../../../utils/checarData";
import formatDateBD from "../../../../utils/formatarDataBD";

import Tabela from "../../../../componentsv2/Tabela";
import ToolbarLotes from "../../Lotes/components/ToolbarLotes";

import { api } from "../../../../services/Api";
import styled from "@emotion/styled";
import Paper from "../../../../componentsv2/Paper";
import { IntervaloDataContext } from "../../Context/IntevervaloDataContext";
import { SincronizacaoFaturamentoContext } from "../../Context/SincronizacaoFaturamentoContext";
import { Context as PermissoesContext } from "../../../../Context/PermissoesContext";
import { obterCodigoConvenio, obterNomeConvenio } from "../../convenio";

const StyledPaper = styled(Paper)(() => ({
  "& .alerta": {
    backgroundColor: "#F5A9A9",
    fontWeight: "bold",
  },
  "& .alerta2": {
    backgroundColor: "#A9F5BC",
    fontWeight: "bold",
    border: "5px solid #F5A9A9",
  },
  "& .atual": {
    backgroundColor: "#A9D0F5",
    fontWeight: "bold",
  },
  "& .melhor": {
    backgroundColor: "#A9F5BC",
    fontWeight: "bold",
  },
  "& .menor": {
    backgroundColor: "#F3E2A9",
    fontWeight: "bold",
  },
}));

const INTERVALO_POLLING = 10000;
const noop = () => {};

const STATUS_PADRAO = "ABERTO";
const STATUS_OPTIONS = [
  { value: "ABERTO", label: "Aberto" },
  { value: "PREPARANDO_XML", label: "Preparando XML" },
  { value: "ENVIADO", label: "Enviado" },
  { value: "CONCLUIDO", label: "Concluído" },
  { value: "PENDENCIA", label: "Pendência" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce(
  (labels, option) => ({ ...labels, [option.value]: option.label }),
  {},
);

const obterRotuloStatus = (status) =>
  STATUS_LABELS[status] || STATUS_LABELS[STATUS_PADRAO];

const TIPOS_LOTE = {
  NORMAL: {
    descricao: "Normal",
    rotulo: "Nor",
    cor: "#2563eb",
  },
  INTERNO: {
    descricao: "Interno",
    rotulo: "Int",
    cor: "#7c3aed",
  },
  PENDENCIA: {
    descricao: "Pendência",
    rotulo: "Pen",
    cor: "#d97706",
  },
  NEGOCIACAO: {
    descricao: "Negociação",
    rotulo: "Neg",
    cor: "#059669",
  },
};

const normalizarTipoLote = (tipo) => {
  const tipoNormalizado = String(tipo || "NORMAL").trim().toUpperCase();

  if (tipoNormalizado.startsWith("INT")) return "INTERNO";
  if (tipoNormalizado.startsWith("PEN")) return "PENDENCIA";
  if (tipoNormalizado.startsWith("NEG")) return "NEGOCIACAO";

  return "NORMAL";
};

function DataPrevisaoEditCell(params) {
  const valor = params.value ? dayjs(params.value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
      <DatePicker
        value={valor?.isValid() ? valor : null}
        onChange={(data) => {
          const valorFormatado = data?.isValid() ? data.format("YYYY-MM-DD") : "";
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: valorFormatado,
          });
        }}
        onAccept={() =>
          params.api.stopCellEditMode({ id: params.id, field: params.field })
        }
        renderInput={(inputParams) => (
          <TextField {...inputParams} autoFocus size="small" fullWidth />
        )}
      />
    </LocalizationProvider>
  );
}

function LotesEmptyState() {
  return (
    <div className="lotes-empty-state" role="status">
      <InboxOutlined aria-hidden="true" />
      <strong>Nenhum lote encontrado</strong>
      <span>Ajuste os filtros ou limpe a pesquisa para tentar novamente.</span>
    </div>
  );
}

function TabelaFaturamento(props) {
  const { enqueueSnackbar } = useSnackbar();
  const { checarPermissaoSemLog } = useContext(PermissoesContext);
  const { dataInicio, dataFim, competencia } = useContext(IntervaloDataContext);
  const { revisaoListagem } = useContext(SincronizacaoFaturamentoContext);

  const {
    visualizacao = "legado",
    setCarregandoTabelaLotes = noop,
    setResultado,
    dadosTabelaFaturamento,
    atualizaTabelaFaturamento,
    competenciaInicial,
    competenciaFinal,
    protocoloFiltro,
    planoFiltro,
    loteFiltro,
    situacaoFiltro,
    setId_lote,
    setCodigo,
    setPlano,
    setAbertura,
    setValor,
    setValorAlterado,
    setMes_competencia,
    setAno_competencia,
    setData_previsao,
    setOpcoes,
    setProtocoloAtual,
    setIdProtocolo,
    setAtualizaTabelaFaturamento,
    setCodigoLote,
    setQuantidade,
    setDataEnvioLote,
    setTipoLote = noop,
  } = props;

  const [carregandoTabela, setCarregandoTabela] = useState(false);
  const sequenciaConsultaRef = useRef(0);
  const intervaloPollingRef = useRef(null);
  const podeEditarStatus = checarPermissaoSemLog("FAT014");

  const consultarLotes = useCallback(
    async ({ silenciosa = false, podeAtualizar = () => true } = {}) => {
      const possuiCompetencia = competenciaInicial && competenciaFinal;
      const possuiPeriodo =
        dataInicio &&
        dataFim &&
        checarData(formatDateBD(dataInicio)) &&
        checarData(formatDateBD(dataFim));

      if (
        !podeAtualizar() ||
        (visualizacao !== "lotes" && !possuiPeriodo)
      ) {
        return;
      }

      const sequenciaConsulta = sequenciaConsultaRef.current + 1;
      sequenciaConsultaRef.current = sequenciaConsulta;

      if (!silenciosa) {
        setCarregandoTabela(true);
        setCarregandoTabelaLotes(true);
      }
      try {
        const paramsBase = {
          ...(visualizacao !== "lotes" && possuiPeriodo && !possuiCompetencia
            ? {
                data_inicial: `${formatDateBD(dataInicio)} 00:00:00`,
                data_final: `${formatDateBD(dataFim)} 23:59:59`,
              }
            : {}),
          ...(competenciaInicial && competenciaFinal
            ? {
                competencia_inicial: competenciaInicial,
                competencia_final: competenciaFinal,
              }
            : competencia
              ? { competencia }
              : {}),
          ...(protocoloFiltro ? { protocolo: protocoloFiltro } : {}),
          ...(loteFiltro ? { lote: loteFiltro } : {}),
          ...(situacaoFiltro ? { status: situacaoFiltro } : {}),
        };

        const planosSelecionados =
          planoFiltro?.length && planoFiltro[0]?.id !== 0
            ? planoFiltro.map(obterCodigoConvenio).filter(Boolean)
            : [];
        const parametrosConsultas = planosSelecionados.length
          ? planosSelecionados.map(plano => ({ ...paramsBase, plano }))
          : [paramsBase];
        const respostas = await Promise.all(
          parametrosConsultas.map(params =>
            api.get(`/v2/faturamento/loteFaturamento`, { params }),
          ),
        );
        const resultados = respostas.flatMap(({ data }) =>
          Array.isArray(data) ? data : [],
        );
        const results = Array.from(
          new Map(
            resultados.map((item, index) => [item?.id ?? `sem-id-${index}`, item]),
          ).values(),
        );

        if (
          podeAtualizar() &&
          sequenciaConsultaRef.current === sequenciaConsulta
        ) {
          setResultado(results);
        }
      } catch (e) {
        if (
          !silenciosa &&
          podeAtualizar() &&
          sequenciaConsultaRef.current === sequenciaConsulta
        ) {
          enqueueSnackbar(
            "Não foi possível encontrar pedidos desta data. Por favor, tente novamente.",
            { variant: "error" },
          );
        }
      } finally {
        if (
          podeAtualizar() &&
          sequenciaConsultaRef.current === sequenciaConsulta
        ) {
          if (!silenciosa) {
            setCarregandoTabela(false);
            setCarregandoTabelaLotes(false);
          }
        }
      }
    },
    [
      competencia,
      competenciaFinal,
      competenciaInicial,
      dataFim,
      dataInicio,
      enqueueSnackbar,
      loteFiltro,
      planoFiltro,
      protocoloFiltro,
      setCarregandoTabelaLotes,
      setResultado,
      situacaoFiltro,
      visualizacao,
    ],
  );

  useEffect(() => {
    let consultaAtiva = true;

    consultarLotes({ podeAtualizar: () => consultaAtiva });

    return () => {
      consultaAtiva = false;
    };
  }, [atualizaTabelaFaturamento, consultarLotes, revisaoListagem]);

  useEffect(() => {
    let pollingAtivo = true;

    const limparIntervalo = () => {
      if (intervaloPollingRef.current !== null) {
        clearInterval(intervaloPollingRef.current);
        intervaloPollingRef.current = null;
      }
    };

    const iniciarIntervalo = () => {
      if (
        !pollingAtivo ||
        document.visibilityState === "hidden" ||
        intervaloPollingRef.current !== null
      ) {
        return;
      }

      intervaloPollingRef.current = setInterval(() => {
        if (document.visibilityState !== "hidden") {
          consultarLotes({
            silenciosa: true,
            podeAtualizar: () => pollingAtivo,
          });
        }
      }, INTERVALO_POLLING);
    };

    const consultarAoRetomar = () => {
      if (!pollingAtivo || document.visibilityState === "hidden") {
        limparIntervalo();
        return;
      }

      consultarLotes({
        silenciosa: true,
        podeAtualizar: () => pollingAtivo,
      });
      iniciarIntervalo();
    };

    const aoAlterarVisibilidade = () => {
      if (document.visibilityState === "hidden") {
        limparIntervalo();
        return;
      }

      consultarAoRetomar();
    };

    const aoRecuperarFoco = () => {
      if (document.visibilityState !== "hidden") {
        consultarAoRetomar();
      }
    };

    iniciarIntervalo();
    window.addEventListener("focus", aoRecuperarFoco);
    document.addEventListener("visibilitychange", aoAlterarVisibilidade);

    return () => {
      pollingAtivo = false;
      limparIntervalo();
      window.removeEventListener("focus", aoRecuperarFoco);
      document.removeEventListener("visibilitychange", aoAlterarVisibilidade);
    };
  }, [consultarLotes]);

  const handleEditClick = useCallback(
    (row) => {
      setIdProtocolo(row?.id_protocolo);
      setId_lote(row.id);
      setCodigoLote(row.lote);
      setCodigo(row.lote);
      setPlano(row.plano);
      setAbertura(row.mes_competencia + "/" + row.ano_competencia);
      setValor(row.valor);
      setValorAlterado(row.valor_alterado);
      setProtocoloAtual(row.protocolo ? row.protocolo : "");
      setMes_competencia(row.mes_competencia);
      setAno_competencia(row.ano_competencia);
      setData_previsao(
        row.data_previsao
          ? format(new Date(row.data_previsao), "yyyy-MM-dd")
          : "",
      );
      setOpcoes(true);
      setQuantidade(row.qtd_guias);
      setDataEnvioLote(
        row.data_envio_lote
          ? format(new Date(row.data_envio_lote), "yyyy-MM-dd")
          : "",
      );
      setTipoLote((row.tipo || "NORMAL").toUpperCase());
    },
    [
      setAbertura,
      setAno_competencia,
      setCodigo,
      setCodigoLote,
      setDataEnvioLote,
      setTipoLote,
      setData_previsao,
      setIdProtocolo,
      setId_lote,
      setMes_competencia,
      setOpcoes,
      setPlano,
      setProtocoloAtual,
      setQuantidade,
      setValor,
      setValorAlterado,
    ],
  );

  const colunas = useMemo(() => {
    const colunaOpcoes = {
      field: "opcoes",
      headerName: "Opções",
      headerAlign: "center",
      align: "center",
      width: 130,
      type: "number",
      editable: false,
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: ({ row }) => (
        <div style={{ cursor: "pointer" }}>
          <Tooltip title={`Ações do lote ${row.lote}`}>
            <IconButton
              aria-label={`Ações do lote ${row.lote}`}
              onClick={() => {
                handleEditClick(row);
              }}
            >
              <i className="fa fa-cog" style={{ color: "#003399" }} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    };

    const colunasBase = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      width: 90,
      hide: true,
    },
    {
      field: "em_construcao",
      headerName: "Em Construção",
      headerAlign: "center",
      align: "center",
      minWidth: 90,
      hide: true,
    },
    {
      field: "lote",
      headerName: "Lote",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 120,
      editable: false,
    },
    {
      field: "qtd_guias",
      headerName: "N° de Guias",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 150,
      editable: false,
    },
    {
      field: "plano",
      headerName: "Plano",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 120,
      editable: false,
      valueGetter: ({ row }) =>
        obterNomeConvenio({
          convenio: row.plano,
          nome_convenio: row.nome_convenio,
        }),
    },
    {
      field: "valor",
      headerName: "Valor",
      type: "number",
      headerAlign: "center",
      align: "center",
      width: 150,
      valueFormatter: ({ value }) =>
        value !== null && value !== undefined && value !== ""
          ? Number(value).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "R$ 0,00",
    },
    {
      field: "data_envio_lote",
      headerName: "Data Envio",
      headerAlign: "center",
      align: "center",
      width: 160,
      editable: false,
      valueFormatter: ({ value }) =>
        value ? `${format(new Date(value), "dd/MM/yyyy")}` : "—",
    },
    {
      field: "protocolo",
      headerName: "Protocolo",
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 130,
      editable: true,
      valueFormatter: ({ value }) => value || "—",
    },
    {
      field: "competencia",
      headerName: "Competência",
      headerAlign: "center",
      align: "center",
      width: 160,
      editable: true,
      valueGetter: (params) => {
        const value = `${
          params.row.mes_competencia ? params.row.mes_competencia : ""
        }/${params.row.ano_competencia ? params.row.ano_competencia : ""}`;
        return value;
      },
    },
    {
      field: "data_previsao",
      headerName: "Data Previsão",
      headerAlign: "center",
      align: "center",
      width: 165,
      editable: true,
      valueParser: (value) => {
        if (!value) return "";
        const texto = String(value).trim();
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(texto)) {
          const [dia, mes, ano] = texto.split("/");
          return `${ano}-${mes}-${dia}`;
        }
        return texto;
      },
      renderEditCell: DataPrevisaoEditCell,
      valueFormatter: ({ value }) =>
        value ? `${format(new Date(value), "dd/MM/yyyy")}` : "—",
    },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "center",
      align: "center",
      width: 180,
      type: "singleSelect",
      valueOptions: STATUS_OPTIONS,
      valueGetter: ({ value }) => value || STATUS_PADRAO,
      editable: podeEditarStatus,
      renderCell: ({ value }) => {
        const status = value || STATUS_PADRAO;
        const cores = {
          ABERTO: "#64748b",
          PREPARANDO_XML: "#d97706",
          ENVIADO: "#15803d",
          CONCLUIDO: "#0369a1",
          PENDENCIA: "#dc2626",
        };
        return (
          <Chip
            size="small"
            label={obterRotuloStatus(status)}
            sx={{
              color: cores[status] || cores.ABERTO,
              backgroundColor: `${cores[status] || cores.ABERTO}18`,
              border: `1px solid ${cores[status] || cores.ABERTO}44`,
              fontWeight: 600,
            }}
          />
        );
      },
    },
    ];

    colunasBase.splice(2, 0, colunaOpcoes);

    if (visualizacao === "lotes") {
      colunasBase.splice(4, 0, {
        field: "tipo",
        headerName: "Tipo",
        headerAlign: "center",
        align: "center",
        width: 80,
        editable: false,
        valueGetter: ({ value }) => normalizarTipoLote(value),
        renderCell: ({ value }) => {
          const tipo = TIPOS_LOTE[value] || TIPOS_LOTE.NORMAL;

          return (
            <Tooltip title={`Tipo de lote: ${tipo.descricao}`}>
              <Chip
                size="small"
                label={tipo.rotulo}
                aria-label={`Tipo de lote: ${tipo.descricao}`}
                sx={{
                  color: tipo.cor,
                  backgroundColor: `${tipo.cor}18`,
                  border: `1px solid ${tipo.cor}44`,
                  fontWeight: 600,
                }}
              />
            </Tooltip>
          );
        },
      });

      const ordemColunasLotes = [
        "plano",
        "lote",
        "tipo",
        "qtd_guias",
        "valor",
        "data_envio_lote",
        "protocolo",
        "competencia",
        "status",
        "opcoes",
      ];
      const colunasPorCampo = new Map(
        colunasBase.map((coluna) => [coluna.field, coluna]),
      );

      return [
        ...colunasBase.filter(({ hide }) => hide),
        ...ordemColunasLotes.map((field) => colunasPorCampo.get(field)),
      ];
    }

    return colunasBase;
  }, [handleEditClick, podeEditarStatus, visualizacao]);

  const processRowUpdate = async (
    newRow,
    oldRow = { ...newRow, status: undefined },
  ) => {
    const statusAnterior = oldRow.status || STATUS_PADRAO;
    const statusAtualizado = newRow.status || STATUS_PADRAO;

    if (statusAtualizado !== statusAnterior) {
      if (!podeEditarStatus) {
        return { ...oldRow, status: statusAnterior };
      }

      try {
        const { data } = await api.patch(
          `/v2/faturamento/lotes/${newRow.id}/status`,
          { status: statusAtualizado },
        );
        const statusConfirmado = data?.status || statusAtualizado;

        setAtualizaTabelaFaturamento((prevState) => !prevState);
        enqueueSnackbar("Status do lote atualizado.", { variant: "success" });

        return { ...newRow, status: statusConfirmado };
      } catch (error) {
        enqueueSnackbar("Não foi possível atualizar o status do lote.", {
          variant: "error",
        });

        return { ...oldRow, status: statusAnterior };
      }
    }

    if (newRow.data_previsao !== oldRow.data_previsao) {
      try {
        const previsao = newRow.data_previsao || null;
        await api.put("/v2/faturamento/adicionaProtocolo", {
          codigo: newRow.lote,
          plano: newRow.plano,
          protocolo: newRow.protocolo,
          previsao,
          mes_competencia: newRow.mes_competencia,
          ano_competencia: newRow.ano_competencia,
        });
        setAtualizaTabelaFaturamento((prevState) => !prevState);
        enqueueSnackbar("Data de previsão atualizada.", { variant: "success" });
        return { ...newRow, data_previsao: previsao };
      } catch (error) {
        enqueueSnackbar("Não foi possível atualizar a data de previsão.", {
          variant: "error",
        });
        return oldRow;
      }
    }

    try {
      let anoCompetencia = "";
      let mesCompetencia = "";

      if (newRow.competencia) {
        anoCompetencia = newRow.competencia.split("/")[1];
        mesCompetencia = newRow.competencia.split("/")[0];

        if (anoCompetencia?.length !== 4 || mesCompetencia?.length !== 2) {
          enqueueSnackbar("Formato do campo competência inválido.", {
            variant: "warning",
          });
          return oldRow;
        }
      } else {
        anoCompetencia = newRow.ano_competencia;
        mesCompetencia = newRow.mes_competencia;
      }

      await api.put(
        `/v2/faturamento/alteraProtocoloCompetencia/${newRow.id_protocolo}`,
        {
          protocolo: newRow.protocolo,
          mes_competencia: mesCompetencia,
          ano_competencia: anoCompetencia,
        }
      );

      setAtualizaTabelaFaturamento((prevState) => !prevState);

      if (newRow.competencia) {
        enqueueSnackbar("Competência atualizada.", { variant: "success" });
      } else {
        enqueueSnackbar("Protocolo atualizado.", { variant: "success" });
      }
      return newRow;
    } catch (error) {
      enqueueSnackbar("Não foi possível atualizar os dados do lote.", {
        variant: "error",
      });
      return oldRow;
    }
  };

  return (
    <>
      <StyledPaper
        className={visualizacao === "lotes" ? "lotes-table" : undefined}
        style={{
          backgroundColor: "White",
          width: "100%",
          ...(visualizacao === "lotes"
            ? {
                height: "clamp(420px, calc(100vh - 280px), 760px)",
                minHeight: "420px",
              }
            : {}),
        }}
      >
        <Tabela
          disableColumnMenu
          {...(visualizacao === "lotes"
          ? {
              components: {
                Toolbar: ToolbarLotes,
                NoRowsOverlay: LotesEmptyState,
              },
              componentsProps: {
                toolbar: {
                  quantidadeResultados: dadosTabelaFaturamento.length,
                },
              },
            }
            : { autoHeight: true })}
          columns={colunas}
          rows={dadosTabelaFaturamento}
          loading={carregandoTabela}
          pageSize={100}
          rowsPerPageOptions={[50, 100]}
          processRowUpdate={processRowUpdate}
          sx={{
            "& .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "center",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "nowrap",
            },
            "& .MuiDataGrid-cell": {
              justifyContent: "center",
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiDataGrid-row": {
              "&.em-construcao": {
                backgroundColor: "#ffe082", // Apply orange background for rows with em_construcao = true
              },
            },
          }}
          getRowClassName={(params) => {
            return params.row.em_construcao ? "em-construcao" : "";
          }}
          initialState={{
            columns: {
              columnVisibilityModel: {
                id: false,
                em_construcao: false,
              },
            },
          }}
        />
      </StyledPaper>
    </>
  );
}

export default memo(TabelaFaturamento);
